import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, ListItemText, MenuItem, Select, Stack } from '@mui/material';

import {
  COMMISSION_RULE_CONDITION_TYPES,
  CommissionOption,
  CommissionsConditionTypes,
  RuleCondition,
  ruleConditionsToTextMap,
} from '~/consts/agentHub';

import AgentHubCommissionsAgencyInput from './AgentHubCommissionsAgencyInput';
import AgentHubCommissionsAgentInput from './AgentHubCommissionsAgentInput';
import AgentHubCommissionsBundleTotalPriceInput from './AgentHubCommissionsBundleTotalPrice';
import AgentHubCommissionsDateRangeInput from './AgentHubCommissionsDateRangeInput';
import AgentHubCommissionsLocationInput from './AgentHubCommissionsLocationInput';
import AgentHubCommissionsMarginInput from './AgentHubCommissionsMarginInput';
import AgentHubCommissionsMinTotalSpendInput from './AgentHubCommissionsMinTotalSpendInput';
import AgentHubCommissionsProductTypeInput from './AgentHubCommissionsProductTypeInput';
import AgentHubCommissionsTextInput from './AgentHubCommissionsTextInput';
import AgentHubCommissionsTierInput from './AgentHubCommissionsTierInput';
import FlightCarriersInput from './FlightCarriersInput';

interface Props {
  addCondition: (condition: RuleCondition, replace?: boolean) => void;
  ruleConditions: CommissionOption;
  conditionType: CommissionsConditionTypes;
  visibleConditions: Set<CommissionsConditionTypes>;
  handleChangeCondition: (prevType: CommissionsConditionTypes, type: CommissionsConditionTypes) => void;
  /** Only required when this row’s condition is (or can become) `flightCarrier`. */
  brand?: string;
  onFlightCarrierChange?: (next: CommissionOption) => void;
}

export function AddNewConditionForm({
  conditionType,
  ruleConditions,
  addCondition,
  handleChangeCondition,
  visibleConditions,
  brand,
  onFlightCarrierChange,
}: Props) {
  const [selectedConditionType, setSelectedConditionType] = useState(conditionType || null);
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Stack direction="column" spacing={1}>
      <Select
        value={selectedConditionType}
        name="condition-type"
        labelId="agent-hub-commissions-condition-type-input-label"
        label="Condition Type"
        sx={{ borderRadius: 0.5 }}
        onChange={(event) => {
          const newConditionType = event.target.value as CommissionsConditionTypes;
          if (visibleConditions.has(newConditionType)) {
            return enqueueSnackbar('This condition is already set', { variant: 'warning' });
          }
          setSelectedConditionType(newConditionType);
          handleChangeCondition(conditionType, newConditionType);
        }}
        renderValue={(value) => ruleConditionsToTextMap.get(value)}
      >
        {COMMISSION_RULE_CONDITION_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            <ListItemText primary={ruleConditionsToTextMap.get(type)} />
          </MenuItem>
        ))}
      </Select>

      {selectedConditionType === 'agency' && (
        <AgentHubCommissionsAgencyInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'agent' && (
        <AgentHubCommissionsAgentInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'location' && (
        <AgentHubCommissionsLocationInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'bundledWithOffer' && (
        <AgentHubCommissionsTextInput
          conditionType="bundledWithOffer"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'flightClass' && (
        <AgentHubCommissionsTextInput
          conditionType="flightClass"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'bundledWithTypes' && (
        <AgentHubCommissionsProductTypeInput
          ruleType="bundledWithTypes"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'productType' && (
        <AgentHubCommissionsProductTypeInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'flightCarrier' &&
        (brand ? (
          <FlightCarriersInput
            brand={brand}
            value={ruleConditions}
            onChange={onFlightCarrierChange ?? (() => undefined)}
          />
        ) : (
          <Alert severity="warning" variant="outlined">
            Select a tenant brand to search airlines.
          </Alert>
        ))}

      {selectedConditionType === 'vendor' && (
        <AgentHubCommissionsTextInput
          conditionType="vendor"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'offer' && (
        <AgentHubCommissionsTextInput
          conditionType="offer"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'dateRange' && (
        <AgentHubCommissionsDateRangeInput
          conditionType="dateRange"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
          description="Please notice that the date will be saved in UTC timezone."
        />
      )}

      {selectedConditionType === 'margin' && (
        <AgentHubCommissionsMarginInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'bundleTotalPrice' && (
        <AgentHubCommissionsBundleTotalPriceInput
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'minTotalSpend' && (
        <AgentHubCommissionsMinTotalSpendInput
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'tier' && (
        <AgentHubCommissionsTierInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}
    </Stack>
  );
}
